import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'
import type { Property } from '@/types'
import {
  listProperties,
  createProperty,
  getProperty,
  updateProperty,
  deleteProperty,
} from '@/services/services'
import { useUiHelpers } from './useUiHelpers'

export const usePropertyStore = defineStore('property', () => {
  const ui = useUiHelpers()

  // Initialize as object with ID keys (matching old Vuex pattern)
  const properties = ref<Record<number, Property>>({})

  // Convert PROPERTIES array to object on initialization
  if (typeof PROPERTIES !== 'undefined' && Array.isArray(PROPERTIES)) {
    PROPERTIES.forEach((p: Property) => {
      properties.value[p.id] = p
    })
  }

  // Getters
  const isNewAccount = computed(
    () => Object.keys(properties.value).length === 0,
  )

  const sanitizedProperties = computed<Property[]>(() =>
    Object.values(properties.value).filter((p): p is Property => p != null),
  )

  const selectedProperty = computed<Property | null>(() => {
    const id = router.currentRoute.value.params.id as string | undefined
    return id ? (properties.value[Number(id)] ?? null) : null
  })

  // Actions
  async function loadProperties() {
    await ui.runAsyncFunction(async () => {
      const { data } = await listProperties()
      data.forEach((p: Property) => (properties.value[p.id] = p))
    })
  }

  async function createPropertyAction(propertyData: Partial<Property>) {
    return await ui.runAsyncFunction(async () => {
      const { data } = await createProperty(propertyData)
      properties.value[data.id] = data
      return data.id
    })
  }

  async function fetchPropertyAction(id: number) {
    await ui.runAsyncFunction(async () => {
      const { data } = await getProperty(id)
      properties.value[data.id] = { ...properties.value[data.id], ...data }
    })
  }

  async function updatePropertyAction(updated: Property) {
    await ui.runAsyncFunction(async () => {
      const { data } = await updateProperty(updated.id, updated)
      properties.value[data.id] = { ...properties.value[data.id], ...data }
    })
  }

  async function destroyPropertyAction(property: Property) {
    await ui.runAsyncFunction(async () => {
      await deleteProperty(property.id)
      delete properties.value[property.id]
    })
  }

  return {
    // state
    properties,
    // getters
    sanitizedProperties,
    isNewAccount,
    selectedProperty,
    // actions
    loadProperties,
    createPropertyAction,
    fetchPropertyAction,
    updatePropertyAction,
    destroyPropertyAction,
  }
})
